import { useState, useEffect, useCallback, useRef } from "react";

// ════════════════════════════════════════════════════════
// GUIDANCE WING COACHING CENTRE — Full CMS Website
// CoachHub-Inspired Premium Design
// ════════════════════════════════════════════════════════

// ─── STORAGE KEYS ───
const K = {
  SITE: "gwcc-site-config",
  FACULTY: "gwcc-faculty",
  COURSES: "gwcc-courses",
  RESULTS: "gwcc-results",
  MATERIALS: "gwcc-materials",
  TESTIMONIALS: "gwcc-testimonials",
  GALLERY: "gwcc-gallery",
  ENQUIRIES: "gwcc-enquiries",
  ADMIN_CREDS: "gwcc-admin-creds",
  STUDENT_PASS: "gwcc-student-pass",
};

const DEFAULT_SITE = {
  name: "Guidance Wing",
  subtitle: "Coaching Centre",
  tagline: "Guiding Today, Building Tomorrow",
  heroTitle: "Where Students Discover Their True Potential",
  heroSubtitle: "Expert coaching for Class 10, 11 & 12 Science students. Structured teaching, regular assessments, and personal mentoring that transforms dreams into achievements.",
  heroBadge: "New Batch Starting — June 2026",
  heroImage: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1400&q=80",
  stat1: "500+", stat1Label: "Students Taught",
  stat2: "95%", stat2Label: "Pass Rate",
  stat3: "50+", stat3Label: "Top Selections",
  stat4: "15+", stat4Label: "Expert Faculty",
  highlight1Title: "Concept-First Teaching", highlight1Desc: "We build understanding from the ground up. No rote learning — only deep, lasting clarity that stays with you through exams and beyond.",
  highlight2Title: "Proven Track Record", highlight2Desc: "Our students consistently rank among toppers in Board exams, JEE, and NEET. Results speak louder than promises.",
  highlight3Title: "Personal Mentoring", highlight3Desc: "Every student gets individual attention. Regular doubt-clearing sessions, one-on-one guidance, and a teacher who knows your name.",
  aboutText: "Guidance Wing Coaching Centre, located at Kwakeithel Laishram Leikai, Imphal, Manipur, is dedicated to providing quality education to Science stream students.\n\nFounded with the vision of bridging the gap between school education and competitive exam preparation, we have helped hundreds of students achieve their academic goals.\n\nOur approach combines structured curriculum delivery with personal care — because we believe every student deserves a teacher who believes in them.",
  aboutImage: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80",
  phone: "7005885842",
  address: "Kwakeithel Laishram Leikai, Imphal, Manipur 795001, India",
  email: "",
  primaryColor: "#1a4f8b",
  accentColor: "#d4a017",
};

const DEFAULT_ADMIN = { username: "admin", password: "admin2026" };
const DEFAULT_STUDENT_PASS = "gwcc2026";

const DEFAULT_FACULTY = [
  { id: "1", name: "Dr. Sharma", subject: "Physics", qualification: "PhD Physics, 15+ years exp", photo: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=300&q=80" },
  { id: "2", name: "Mrs. Devi", subject: "Chemistry", qualification: "MSc Chemistry, 12+ years exp", photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&q=80" },
  { id: "3", name: "Mr. Singh", subject: "Mathematics", qualification: "MTech, 10+ years exp", photo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&q=80" },
  { id: "4", name: "Dr. Laishram", subject: "Biology", qualification: "PhD Biology, 8+ years exp", photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&q=80" },
];

const DEFAULT_COURSES = [
  { id: "1", cls: "Class 10", tagline: "Strong Foundation for Board Exams", subjects: "Mathematics, Science, English, Social Science", features: "Board exam focused preparation\nWeekly mock tests\nNTSE & Olympiad guidance\nDoubt clearing sessions", timing: "2:00 PM – 4:00 PM", fee: "₹3,000/month", badge: "" },
  { id: "2", cls: "Class 11", tagline: "Build Concepts for Competitive Edge", subjects: "Physics, Chemistry, Mathematics, Biology", features: "Concept clarity through structured teaching\nRegular tests & performance analysis\nCompetitive exam foundation (JEE/NEET)\nPersonal care & doubt solving", timing: "4:30 PM – 7:30 PM", fee: "₹4,000/month", badge: "New Batch – 01 June 2026" },
  { id: "3", cls: "Class 12", tagline: "Excel in Boards & Entrances", subjects: "Physics, Chemistry, Mathematics, Biology", features: "Board + JEE/NEET integrated coaching\nPrevious year paper analysis\nWeekly full-length tests\nOne-on-one mentoring", timing: "4:30 PM – 7:30 PM", fee: "₹4,500/month", badge: "" },
];

const DEFAULT_RESULTS = [
  { id: "1", name: "Thangjam Romio", exam: "Board Exam 2025", score: "95.6%", cls: "Class 12", rank: "School Topper" },
  { id: "2", name: "Ningthoujam Priya", exam: "Board Exam 2025", score: "93.2%", cls: "Class 12", rank: "District Top 10" },
  { id: "3", name: "Laishram Bikram", exam: "JEE Mains 2025", score: "98.4 %ile", cls: "Class 12", rank: "AIR 4521" },
  { id: "4", name: "Moirangthem Sonia", exam: "NEET 2025", score: "645/720", cls: "Class 12", rank: "State Topper" },
  { id: "5", name: "Khaidem Tomba", exam: "Board Exam 2025", score: "91.8%", cls: "Class 10", rank: "School Topper" },
  { id: "6", name: "Yumnam Chanu", exam: "Board Exam 2025", score: "94.0%", cls: "Class 10", rank: "District Top 5" },
];

const DEFAULT_TESTIMONIALS = [
  { id: "1", name: "Laishram Bikram", cls: "Class 12 (2025)", text: "The faculty at Guidance Wing truly care about each student. Their structured teaching helped me crack JEE Mains with a great score. Forever grateful!", rating: 5 },
  { id: "2", name: "Moirangthem Sonia", cls: "Class 12 (2025)", text: "NEET preparation here was incredible. The regular tests and personal attention made all the difference. I became the state topper!", rating: 5 },
  { id: "3", name: "Thangjam Romio", cls: "Class 12 (2025)", text: "From average marks to school topper — Guidance Wing transformed my academic journey. The doubt-solving sessions were a game changer.", rating: 5 },
  { id: "4", name: "Ningthoujam Priya", cls: "Class 12 (2025)", text: "Best coaching centre in Imphal. The teachers explain concepts so clearly that even difficult topics become easy. Highly recommended!", rating: 4 },
];

const DEFAULT_GALLERY = [
  { id: "1", caption: "Classroom Session", category: "classroom", photo: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=600&q=80" },
  { id: "2", caption: "Science Lab", category: "lab", photo: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=600&q=80" },
  { id: "3", caption: "Annual Day", category: "events", photo: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80" },
  { id: "4", caption: "Group Photo 2025", category: "students", photo: "https://images.unsplash.com/photo-1529390079861-591de354faf5?w=600&q=80" },
  { id: "5", caption: "Award Ceremony", category: "events", photo: "https://images.unsplash.com/photo-1559223607-a43c990c692c?w=600&q=80" },
  { id: "6", caption: "Physics Demo", category: "lab", photo: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=600&q=80" },
];

const SUBJECTS_LIST = ["Physics", "Chemistry", "Mathematics", "Biology", "English", "Computer Science"];
const CLASSES_LIST = ["Class 10", "Class 11", "Class 12"];

// ─── STORAGE HELPERS (Firebase Firestore) ───
import { storageGet, storageSet, uploadFile } from "./firebase";

// ─── ICONS ───
const I = {
  Home:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  Users:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Book:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  Award:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>,
  File:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  Image:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  Msg:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  Phone:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  Map:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Clock:()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Check:()=><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  Star:()=><svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  Plus:()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Trash:()=><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  Edit:()=><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Lock:()=><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Menu:()=><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  X:()=><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Settings:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  Inbox:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  Shield:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Eye:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Upload:()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  Arrow:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
};

// ─── CSS ───
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Sora:wght@300;400;500;600;700;800&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
:root{--pri:#0f3d6e;--pri-l:#2563eb;--pri-ll:#60a5fa;--pri-100:#dbeafe;--pri-50:#eff6ff;--acc:#e8a019;--acc-l:#fbbf24;--w:#fff;--g50:#f8fafc;--g100:#f1f5f9;--g200:#e2e8f0;--g300:#cbd5e1;--g400:#94a3b8;--g500:#64748b;--g600:#475569;--g700:#334155;--g800:#1e293b;--g900:#0f172a;--grn:#22c55e;--red:#ef4444;--fh:'Sora',system-ui,sans-serif;--fb:'Outfit',system-ui,sans-serif;--r:16px;--mx:1200px}
html{scroll-behavior:smooth}
body{font-family:var(--fb);color:var(--g800);background:#fff;overflow-x:hidden}
.app{min-height:100vh;display:flex;flex-direction:column}

/* ─── NAV ─── */
.nav{position:fixed;top:0;left:0;right:0;z-index:100;transition:all .4s}
.nav.scrolled{background:rgba(255,255,255,.96);backdrop-filter:blur(20px);box-shadow:0 1px 20px rgba(0,0,0,.06)}
.nav-in{max-width:var(--mx);margin:0 auto;padding:0 32px;display:flex;align-items:center;justify-content:space-between;height:72px}
.nav-brand{display:flex;align-items:center;gap:12px;cursor:pointer;text-decoration:none}
.nav-logo{width:42px;height:42px;background:linear-gradient(135deg,var(--pri),#1e40af);border-radius:12px;display:flex;align-items:center;justify-content:center;color:var(--acc-l);font-family:var(--fh);font-weight:800;font-size:15px}
.nav-t{font-family:var(--fh);font-weight:700;font-size:15px;transition:.3s}
.nav.scrolled .nav-t{color:var(--g900)}
.nav-t:not(.scrolled *){color:#fff}
.nav-t span{display:block;font-family:var(--fb);font-weight:400;font-size:11px;opacity:.65;letter-spacing:1px;text-transform:uppercase}
.nav-links{display:flex;gap:4px;align-items:center}
.nl{padding:8px 16px;font-size:14px;font-weight:500;cursor:pointer;border-radius:10px;border:none;background:none;font-family:var(--fb);transition:.2s}
.nav:not(.scrolled) .nl{color:rgba(255,255,255,.8)}
.nav:not(.scrolled) .nl:hover,.nav:not(.scrolled) .nl.act{color:#fff;background:rgba(255,255,255,.12)}
.nav.scrolled .nl{color:var(--g600)}
.nav.scrolled .nl:hover,.nav.scrolled .nl.act{color:var(--pri-l);background:var(--pri-50)}
.nav-cta{padding:10px 24px;background:var(--pri-l);color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;font-family:var(--fb);transition:.3s}
.nav-cta:hover{background:var(--pri);transform:translateY(-1px);box-shadow:0 6px 20px rgba(37,99,235,.3)}
.mob-tog{display:none;background:none;border:none;cursor:pointer}
.nav:not(.scrolled) .mob-tog{color:#fff}
.nav.scrolled .mob-tog{color:var(--g700)}
.mob-menu{display:none;position:fixed;top:72px;left:0;right:0;background:#fff;padding:16px 24px;box-shadow:0 8px 32px rgba(0,0,0,.1);border-bottom-left-radius:20px;border-bottom-right-radius:20px}
.mob-menu.open{display:flex;flex-direction:column;gap:4px}
.mob-menu .nl{color:var(--g700);text-align:left;padding:12px 16px}
.mob-menu .nl.act{background:var(--pri-50);color:var(--pri-l)}
@media(max-width:900px){.nav-links{display:none}.mob-tog{display:flex}}

/* ─── HERO ─── */
.hero{position:relative;min-height:100vh;display:flex;align-items:center;overflow:hidden}
.hero-bg{position:absolute;inset:0;z-index:0}
.hero-bg img{width:100%;height:100%;object-fit:cover}
.hero-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(15,23,42,.82) 0%,rgba(15,61,110,.7) 50%,rgba(37,99,235,.4) 100%)}
.hero-in{position:relative;z-index:1;max-width:var(--mx);margin:0 auto;padding:120px 32px 80px;width:100%}
.hero-badge{display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,.1);backdrop-filter:blur(10px);border:1px solid rgba(255,255,255,.15);padding:8px 20px;border-radius:50px;color:var(--acc-l);font-size:13px;font-weight:600;letter-spacing:.3px;margin-bottom:28px;animation:fadeUp .8s ease}
.hero-dot{width:8px;height:8px;border-radius:50%;background:var(--acc-l);animation:pulse 2s infinite}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
.hero h1{font-family:var(--fh);font-size:clamp(36px,5.5vw,64px);font-weight:800;color:#fff;line-height:1.1;max-width:700px;margin-bottom:24px;animation:fadeUp .8s ease .1s both}
.hero p.hero-sub{font-size:clamp(16px,1.8vw,19px);color:rgba(200,220,255,.85);line-height:1.7;max-width:540px;margin-bottom:36px;animation:fadeUp .8s ease .2s both}
.hero-btns{display:flex;gap:14px;flex-wrap:wrap;animation:fadeUp .8s ease .3s both}
.bp{padding:14px 32px;background:var(--pri-l);color:#fff;border:none;border-radius:12px;font-size:15px;font-weight:600;cursor:pointer;font-family:var(--fb);transition:.3s;display:inline-flex;align-items:center;gap:8px}
.bp:hover{background:#1d4ed8;transform:translateY(-2px);box-shadow:0 10px 30px rgba(37,99,235,.35)}
.bo{padding:14px 32px;background:rgba(255,255,255,.08);color:#fff;border:1px solid rgba(255,255,255,.2);border-radius:12px;font-size:15px;font-weight:600;cursor:pointer;font-family:var(--fb);transition:.3s;backdrop-filter:blur(10px)}
.bo:hover{background:rgba(255,255,255,.15);border-color:rgba(255,255,255,.35)}

/* ─── STATS BAR ─── */
.stats-bar{background:#fff;border-radius:20px;max-width:var(--mx);margin:-60px auto 0;position:relative;z-index:2;padding:40px 48px;display:grid;grid-template-columns:repeat(4,1fr);gap:24px;box-shadow:0 20px 60px rgba(0,0,0,.1)}
.stat-i{text-align:center}
.stat-n{font-family:var(--fh);font-size:clamp(28px,3.5vw,42px);font-weight:800;color:var(--pri);line-height:1}
.stat-l{font-size:14px;color:var(--g500);margin-top:6px;font-weight:500}
@media(max-width:768px){.stats-bar{grid-template-columns:repeat(2,1fr);margin:-40px 20px 0;padding:28px 20px;gap:20px}.stat-n{font-size:28px}}

/* ─── SECTIONS ─── */
.sec{padding:100px 32px}
.sec-in{max-width:var(--mx);margin:0 auto}
.sec-hd{margin-bottom:56px}
.sec-hd.center{text-align:center}
.sec-lab{font-size:13px;font-weight:700;color:var(--pri-l);letter-spacing:2.5px;text-transform:uppercase;margin-bottom:12px;font-family:var(--fh)}
.sec-hd h2{font-family:var(--fh);font-size:clamp(28px,3.5vw,42px);font-weight:800;color:var(--g900);line-height:1.15}
.sec-hd p{font-size:17px;color:var(--g500);line-height:1.7;max-width:560px;margin-top:14px}
.sec-hd.center p{margin-left:auto;margin-right:auto}
@media(max-width:768px){.sec{padding:64px 20px}}

/* ─── HIGHLIGHTS ─── */
.hl-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
.hl-c{padding:36px 28px;border-radius:20px;background:var(--g50);border:1px solid var(--g200);transition:.4s}
.hl-c:hover{transform:translateY(-4px);box-shadow:0 16px 48px rgba(0,0,0,.08);border-color:var(--pri-100)}
.hl-num{font-family:var(--fh);font-size:48px;font-weight:800;color:var(--pri-100);margin-bottom:16px;line-height:1}
.hl-c h3{font-family:var(--fh);font-size:19px;font-weight:700;color:var(--g900);margin-bottom:10px}
.hl-c p{font-size:15px;color:var(--g500);line-height:1.65}
@media(max-width:768px){.hl-grid{grid-template-columns:1fr}}

/* ─── ABOUT ─── */
.about-grid{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center}
.about-img{border-radius:24px;overflow:hidden;aspect-ratio:4/3;position:relative}
.about-img img{width:100%;height:100%;object-fit:cover}
.about-img::after{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(37,99,235,.1),transparent);border-radius:24px}
.about-txt h2{font-family:var(--fh);font-size:clamp(26px,3vw,36px);font-weight:800;color:var(--g900);margin-bottom:20px;line-height:1.2}
.about-txt p{font-size:16px;color:var(--g500);line-height:1.75;margin-bottom:14px}
@media(max-width:768px){.about-grid{grid-template-columns:1fr;gap:32px}}

/* ─── FACULTY ─── */
.fac-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:20px}
.fac-c{background:#fff;border:1px solid var(--g200);border-radius:20px;padding:28px;text-align:center;transition:.3s}
.fac-c:hover{transform:translateY(-4px);box-shadow:0 12px 40px rgba(0,0,0,.08)}
.fac-img{width:80px;height:80px;border-radius:50%;margin:0 auto 16px;overflow:hidden;border:3px solid var(--pri-100)}
.fac-img img{width:100%;height:100%;object-fit:cover}
.fac-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,var(--pri-l),var(--pri));color:#fff;font-weight:700;font-size:22px;font-family:var(--fh)}
.fac-c h4{font-family:var(--fh);font-size:17px;font-weight:700;color:var(--g900)}
.fac-sub{font-size:13px;color:var(--pri-l);font-weight:600;margin-top:3px}
.fac-qual{font-size:13px;color:var(--g400);margin-top:4px}

/* ─── COURSES ─── */
.co-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:24px}
.co-c{background:#fff;border:1px solid var(--g200);border-radius:20px;overflow:hidden;transition:.3s;position:relative}
.co-c:hover{transform:translateY(-4px);box-shadow:0 16px 48px rgba(0,0,0,.08)}
.co-c.feat{border-color:var(--pri-l);box-shadow:0 0 0 1px var(--pri-l)}
.co-badge{position:absolute;top:16px;right:16px;background:var(--pri-l);color:#fff;padding:5px 14px;border-radius:50px;font-size:11px;font-weight:700;letter-spacing:.3px}
.co-top{padding:32px 28px 0}
.co-top h3{font-family:var(--fh);font-size:28px;font-weight:800;color:var(--g900)}
.co-top p{font-size:14px;color:var(--g500);margin-top:4px}
.co-body{padding:20px 28px 28px}
.co-subjs{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:20px}
.co-st{background:var(--pri-50);color:var(--pri-l);padding:4px 12px;border-radius:50px;font-size:12px;font-weight:500}
.co-feats{list-style:none;margin-bottom:20px}
.co-feats li{padding:6px 0;font-size:14px;color:var(--g600);display:flex;align-items:flex-start;gap:8px}
.co-feats li svg{flex-shrink:0;color:var(--pri-l);margin-top:2px}
.co-meta{display:flex;justify-content:space-between;align-items:center;padding-top:16px;border-top:1px solid var(--g200)}
.co-time{font-size:13px;color:var(--g500);display:flex;align-items:center;gap:5px}
.co-fee{font-family:var(--fh);font-size:22px;font-weight:800;color:var(--pri)}

/* ─── RESULTS ─── */
.res-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
.res-c{background:#fff;border:1px solid var(--g200);border-radius:var(--r);padding:24px;position:relative;overflow:hidden;transition:.3s}
.res-c:hover{box-shadow:0 8px 24px rgba(0,0,0,.06);transform:translateY(-2px)}
.res-c::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--pri-l),var(--acc-l))}
.res-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px}
.res-nm{font-weight:700;font-size:16px;color:var(--g900);font-family:var(--fh)}
.res-ex{font-size:12px;color:var(--g400)}
.res-sc{font-family:var(--fh);font-size:30px;font-weight:800;color:var(--pri-l)}
.res-rk{display:inline-block;margin-top:8px;background:var(--pri-50);color:var(--pri-l);padding:4px 12px;border-radius:50px;font-size:12px;font-weight:600}
.res-cls{font-size:11px;color:var(--g400)}

/* ─── TESTIMONIALS ─── */
.test-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:24px}
.test-c{background:#fff;border:1px solid var(--g200);border-radius:20px;padding:32px;position:relative;transition:.3s}
.test-c:hover{box-shadow:0 12px 40px rgba(0,0,0,.06)}
.test-c::before{content:'\u201C';position:absolute;top:16px;right:24px;font-family:var(--fh);font-size:72px;color:var(--pri-100);line-height:1}
.test-stars{display:flex;gap:3px;margin-bottom:14px}
.test-txt{font-size:15px;color:var(--g600);line-height:1.7;margin-bottom:24px}
.test-au{display:flex;align-items:center;gap:12px}
.test-av{width:42px;height:42px;border-radius:50%;background:linear-gradient(135deg,var(--pri-ll),var(--pri));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:14px;font-family:var(--fh)}
.test-nm{font-size:15px;font-weight:700;color:var(--g900);font-family:var(--fh)}
.test-cl{font-size:12px;color:var(--g400)}

/* ─── GALLERY ─── */
.gal-filters{display:flex;gap:8px;margin-bottom:32px;flex-wrap:wrap}
.gf{padding:8px 20px;border:1px solid var(--g300);border-radius:50px;font-size:13px;font-weight:500;cursor:pointer;background:none;font-family:var(--fb);transition:.2s;color:var(--g600)}
.gf.act{background:var(--pri-l);color:#fff;border-color:var(--pri-l)}
.gal-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
.gal-i{border-radius:16px;overflow:hidden;aspect-ratio:4/3;position:relative;cursor:pointer;transition:.4s}
.gal-i:hover{transform:scale(1.02)}
.gal-i img{width:100%;height:100%;object-fit:cover;transition:.4s}
.gal-i:hover img{transform:scale(1.08)}
.gal-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,var(--pri-100),var(--pri-50));font-size:40px}
.gal-ov{position:absolute;bottom:0;left:0;right:0;padding:16px 20px;background:linear-gradient(transparent,rgba(0,0,0,.6));color:#fff;font-size:14px;font-weight:500}

/* ─── MATERIALS ─── */
.mat-login{max-width:400px;margin:0 auto;background:#fff;border:1px solid var(--g200);border-radius:24px;padding:40px;text-align:center}
.mat-login h3{font-family:var(--fh);font-size:24px;color:var(--g900);margin-bottom:8px}
.mat-login p{font-size:14px;color:var(--g500);margin-bottom:24px}
.mat-panel{display:grid;grid-template-columns:240px 1fr;gap:24px}
.mat-sb{background:#fff;border:1px solid var(--g200);border-radius:var(--r);padding:20px}
.mat-sb h4{font-size:11px;font-weight:700;color:var(--g400);text-transform:uppercase;letter-spacing:1px;margin-bottom:10px}
.sb-i{padding:9px 14px;border-radius:10px;font-size:13.5px;color:var(--g600);cursor:pointer;transition:.15s;display:flex;align-items:center;gap:8px}
.sb-i:hover{background:var(--pri-50);color:var(--pri-l)}
.sb-i.act{background:var(--pri-100);color:var(--pri-l);font-weight:600}
.mat-ct{background:#fff;border:1px solid var(--g200);border-radius:var(--r);padding:28px}
.mat-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px}
.mat-hdr h3{font-family:var(--fh);font-size:20px;color:var(--g900)}
@media(max-width:768px){.mat-panel{grid-template-columns:1fr}}

/* ─── CONTACT ─── */
.ct-grid{display:grid;grid-template-columns:1fr 1fr;gap:48px;align-items:start}
.ct-info h3{font-family:var(--fh);font-size:28px;color:var(--g900);margin-bottom:16px}
.ct-info>p{font-size:16px;color:var(--g500);line-height:1.7;margin-bottom:32px}
.ct-det{display:flex;align-items:flex-start;gap:14px;margin-bottom:24px}
.ct-det-ic{width:44px;height:44px;border-radius:12px;background:var(--pri-50);color:var(--pri-l);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ct-det h4{font-size:14px;font-weight:600;color:var(--g800);font-family:var(--fh)}
.ct-det p{font-size:14px;color:var(--g500)}
.ct-form{background:#fff;border:1px solid var(--g200);border-radius:24px;padding:36px}
.ct-form h3{font-family:var(--fh);font-size:22px;color:var(--g900);margin-bottom:24px}
@media(max-width:768px){.ct-grid{grid-template-columns:1fr;gap:32px}}

/* ─── CTA BANNER ─── */
.cta-banner{background:linear-gradient(135deg,var(--pri) 0%,#1e40af 100%);border-radius:28px;padding:64px 48px;text-align:center;margin:0 32px}
.cta-banner h2{font-family:var(--fh);font-size:clamp(26px,3.5vw,38px);font-weight:800;color:#fff;margin-bottom:14px}
.cta-banner p{font-size:17px;color:rgba(200,220,255,.8);margin-bottom:32px;max-width:480px;margin-left:auto;margin-right:auto}
.cta-banner .bp{background:#fff;color:var(--pri);font-size:16px;padding:16px 36px}
.cta-banner .bp:hover{box-shadow:0 10px 30px rgba(0,0,0,.2);transform:translateY(-2px)}

/* ─── FOOTER ─── */
.footer{background:var(--g900);color:rgba(200,210,230,.7);padding:64px 32px 24px;margin-top:100px}
.footer-in{max-width:var(--mx);margin:0 auto}
.footer-grid{display:grid;grid-template-columns:2fr 1fr 1fr;gap:48px;margin-bottom:48px}
.footer h4{color:#fff;font-family:var(--fh);font-size:16px;margin-bottom:16px;font-weight:700}
.footer p{font-size:14px;line-height:1.7}
.fl{list-style:none}.fl li{padding:4px 0}.fl a{color:rgba(200,210,230,.7);font-size:14px;text-decoration:none;cursor:pointer;transition:.2s}.fl a:hover{color:var(--acc-l)}
.footer-bot{border-top:1px solid rgba(255,255,255,.08);padding-top:20px;text-align:center;font-size:13px;color:rgba(200,210,230,.4)}
@media(max-width:768px){.footer-grid{grid-template-columns:1fr;gap:32px}}

/* ─── FORM INPUTS ─── */
.ig{margin-bottom:14px;text-align:left}
.ig label{font-size:13px;font-weight:600;color:var(--g700);display:block;margin-bottom:5px;font-family:var(--fh)}
.inf{width:100%;padding:12px 16px;border:1px solid var(--g300);border-radius:12px;font-size:14px;font-family:var(--fb);outline:none;transition:.2s;background:#fff}
.inf:focus{border-color:var(--pri-l);box-shadow:0 0 0 3px var(--pri-100)}
textarea.inf{resize:vertical;min-height:80px}
select.inf{appearance:auto}
.bf{width:100%;padding:13px;background:var(--pri-l);color:#fff;border:none;border-radius:12px;font-size:15px;font-weight:600;cursor:pointer;font-family:var(--fb);transition:.2s}
.bf:hover{background:var(--pri)}
.err{color:var(--red);font-size:13px;margin-top:6px}
.succ{background:#dcfce7;color:#166534;padding:12px 16px;border-radius:12px;font-size:14px;margin-bottom:14px}
.f-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
@media(max-width:768px){.f-row{grid-template-columns:1fr}}
.empty{text-align:center;padding:48px 16px;color:var(--g400)}
.fade{animation:fadeUp .5s ease}

/* ─── ADMIN (kept same) ─── */
.admin-wrap{display:flex;min-height:100vh;background:var(--g50)}
.admin-sb{width:240px;background:var(--g900);color:#fff;padding:20px 0;flex-shrink:0;display:flex;flex-direction:column}
.admin-sb-hd{padding:0 20px 20px;border-bottom:1px solid rgba(255,255,255,.1)}
.admin-sb-hd h3{font-family:var(--fh);font-size:17px;font-weight:700}
.admin-sb-hd p{font-size:11px;opacity:.5;margin-top:2px}
.admin-nav{flex:1;padding:12px 8px}
.an-i{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;cursor:pointer;font-size:14px;color:rgba(255,255,255,.55);transition:.15s;margin-bottom:2px}
.an-i:hover{background:rgba(255,255,255,.08);color:#fff}
.an-i.act{background:rgba(255,255,255,.12);color:#fff;font-weight:600}
.admin-sb-ft{padding:12px 20px;border-top:1px solid rgba(255,255,255,.1)}
.admin-main{flex:1;padding:28px 32px;overflow-y:auto;max-height:100vh}
.admin-main h2{font-family:var(--fh);font-size:26px;color:var(--g900);margin-bottom:20px}
.admin-main h3{font-family:var(--fh);font-size:18px;color:var(--g900);margin-bottom:14px}
.a-card{background:#fff;border:1px solid var(--g200);border-radius:var(--r);padding:24px;margin-bottom:16px}
.a-card h4{font-size:14px;font-weight:600;color:var(--g800);margin-bottom:14px}
.a-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px}
.a-row-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:12px}
.btn{padding:9px 18px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;font-family:var(--fb);transition:.2s;display:inline-flex;align-items:center;gap:6px;border:none}
.btn-p{background:var(--pri-l);color:#fff}.btn-p:hover{background:var(--pri)}
.btn-d{background:none;color:var(--red);border:1px solid var(--red)}.btn-d:hover{background:#fef2f2}
.btn-o{background:none;color:var(--g600);border:1px solid var(--g300)}.btn-o:hover{border-color:var(--pri-ll);color:var(--pri-l)}
.btn-g{background:var(--grn);color:#fff}.btn-g:hover{opacity:.9}
.a-list-i{display:flex;align-items:center;justify-content:space-between;padding:12px;border:1px solid var(--g200);border-radius:10px;margin-bottom:6px}
.a-list-i:hover{background:var(--g50)}
.a-tag{display:inline-block;padding:2px 8px;border-radius:50px;font-size:11px;font-weight:600;background:var(--pri-100);color:var(--pri-l)}
.img-upload{border:2px dashed var(--g300);border-radius:var(--r);padding:20px;text-align:center;cursor:pointer;transition:.2s;position:relative;overflow:hidden}
.img-upload:hover{border-color:var(--pri-ll);background:var(--pri-50)}
.img-upload input{position:absolute;inset:0;opacity:0;cursor:pointer}
.img-upload img{max-width:100%;max-height:120px;border-radius:10px;object-fit:cover}
.img-upload p{font-size:12px;color:var(--g400);margin-top:6px}
@media(max-width:768px){.admin-wrap{flex-direction:column}.admin-sb{width:100%;flex-direction:row;padding:12px;overflow-x:auto}.admin-sb-hd{display:none}.admin-nav{display:flex;gap:4px;padding:0}.an-i{white-space:nowrap;padding:8px 12px;font-size:12px}.admin-sb-ft{display:none}.admin-main{padding:16px}.a-row,.a-row-3{grid-template-columns:1fr}}
`;

// Image upload helper
function ImageUpload({ value, onChange, label }) {
  const ref = useRef();
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Max 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="img-upload" onClick={() => ref.current?.click()}>
      <input ref={ref} type="file" accept="image/*" onChange={handleFile} />
      {value ? <img src={value} alt="" /> : <><I.Upload /><p>{label || "Click to upload (max 2MB)"}</p></>}
    </div>
  );
}

// ════════════════════════════════════════════════════════
// MAIN APP
// ════════════════════════════════════════════════════════
export default function App() {
  const [route, setRoute] = useState(window.location.hash === "#admin" ? "admin" : "public");
  const [loaded, setLoaded] = useState(false);
  const [site, setSite] = useState(DEFAULT_SITE);
  const [faculty, setFaculty] = useState(DEFAULT_FACULTY);
  const [courses, setCourses] = useState(DEFAULT_COURSES);
  const [results, setResults] = useState(DEFAULT_RESULTS);
  const [materials, setMaterials] = useState([]);
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [gallery, setGallery] = useState(DEFAULT_GALLERY);
  const [enquiries, setEnquiries] = useState([]);
  const [adminCreds, setAdminCreds] = useState(DEFAULT_ADMIN);
  const [studentPass, setStudentPass] = useState(DEFAULT_STUDENT_PASS);

  useEffect(() => {
    (async () => {
      const [s,f,c,r,m,t,g,e,ac,sp] = await Promise.all([
        storageGet(K.SITE, DEFAULT_SITE), storageGet(K.FACULTY, DEFAULT_FACULTY),
        storageGet(K.COURSES, DEFAULT_COURSES), storageGet(K.RESULTS, DEFAULT_RESULTS),
        storageGet(K.MATERIALS, []), storageGet(K.TESTIMONIALS, DEFAULT_TESTIMONIALS),
        storageGet(K.GALLERY, DEFAULT_GALLERY), storageGet(K.ENQUIRIES, []),
        storageGet(K.ADMIN_CREDS, DEFAULT_ADMIN), storageGet(K.STUDENT_PASS, DEFAULT_STUDENT_PASS)
      ]);
      setSite(s);setFaculty(f);setCourses(c);setResults(r);setMaterials(m);
      setTestimonials(t);setGallery(g);setEnquiries(e);setAdminCreds(ac);setStudentPass(sp);
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(window.location.hash === "#admin" ? "admin" : "public");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const save = useCallback(async (key, val) => { await storageSet(key, val); }, []);
  const updateSite = (s) => { setSite(s); save(K.SITE, s); };
  const updateFaculty = (f) => { setFaculty(f); save(K.FACULTY, f); };
  const updateCourses = (c) => { setCourses(c); save(K.COURSES, c); };
  const updateResults = (r) => { setResults(r); save(K.RESULTS, r); };
  const updateMaterials = (m) => { setMaterials(m); save(K.MATERIALS, m); };
  const updateTestimonials = (t) => { setTestimonials(t); save(K.TESTIMONIALS, t); };
  const updateGallery = (g) => { setGallery(g); save(K.GALLERY, g); };
  const updateEnquiries = (e) => { setEnquiries(e); save(K.ENQUIRIES, e); };
  const updateAdminCreds = (a) => { setAdminCreds(a); save(K.ADMIN_CREDS, a); };
  const updateStudentPass = (s) => { setStudentPass(s); save(K.STUDENT_PASS, s); };

  if (!loaded) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}><style>{CSS}</style><p style={{fontFamily:'var(--fb)',color:'var(--g500)'}}>Loading...</p></div>;

  const data = { site, faculty, courses, results, materials, testimonials, gallery, enquiries, studentPass, adminCreds };
  const updaters = { updateSite, updateFaculty, updateCourses, updateResults, updateMaterials, updateTestimonials, updateGallery, updateEnquiries, updateStudentPass, updateAdminCreds };

  return route === "admin"
    ? <><style>{CSS}</style><AdminApp data={data} updaters={updaters} /></>
    : <><style>{CSS}</style><PublicApp data={data} updaters={{ updateMaterials, updateEnquiries }} /></>;
}

// ════════════════════════════════════════════════════════
// PUBLIC WEBSITE — CoachHub-Inspired Design
// ════════════════════════════════════════════════════════
function PublicApp({ data, updaters }) {
  const { site, faculty, courses, results, materials, testimonials, gallery, studentPass } = data;
  const [page, setPage] = useState("home");
  const [mob, setMob] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = (p) => { setPage(p); setMob(false); window.scrollTo({top:0}); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    {id:"home",label:"Home"},{id:"about",label:"About"},{id:"courses",label:"Courses"},
    {id:"results",label:"Results"},{id:"materials",label:"Materials"},{id:"gallery",label:"Gallery"},
    {id:"testimonials",label:"Reviews"},{id:"contact",label:"Contact"},
  ];

  return (
    <div className="app">
      <nav className={`nav ${scrolled||page!=='home'?'scrolled':''}`}>
        <div className="nav-in">
          <div className="nav-brand" onClick={()=>nav("home")}>
            <div className="nav-logo">{site.name?.split(' ').map(w=>w[0]).join('').slice(0,2)}</div>
            <div className="nav-t">{site.name}<span>{site.subtitle}</span></div>
          </div>
          <div className="nav-links">
            {navItems.map(n=><button key={n.id} className={`nl ${page===n.id?'act':''}`} onClick={()=>nav(n.id)}>{n.label}</button>)}
            <button className="nav-cta" onClick={()=>nav("contact")}>Enroll Now</button>
          </div>
          <button className="mob-tog" onClick={()=>setMob(!mob)}>{mob?<I.X/>:<I.Menu/>}</button>
        </div>
        <div className={`mob-menu ${mob?'open':''}`}>
          {navItems.map(n=><button key={n.id} className={`nl ${page===n.id?'act':''}`} onClick={()=>nav(n.id)}>{n.label}</button>)}
          <button className="nav-cta" style={{marginTop:8}} onClick={()=>nav("contact")}>Enroll Now</button>
        </div>
      </nav>

      <main style={{flex:1}}>
        {page==="home"&&<PHome site={site} faculty={faculty} courses={courses} testimonials={testimonials} nav={nav}/>}
        {page==="about"&&<PAbout site={site} faculty={faculty}/>}
        {page==="courses"&&<PCourses courses={courses} nav={nav}/>}
        {page==="results"&&<PResults results={results}/>}
        {page==="materials"&&<PMaterials materials={materials} studentPass={studentPass} updateMaterials={updaters.updateMaterials}/>}
        {page==="gallery"&&<PGallery gallery={gallery}/>}
        {page==="testimonials"&&<PTestimonials testimonials={testimonials}/>}
        {page==="contact"&&<PContact site={site} enquiries={data.enquiries} updateEnquiries={updaters.updateEnquiries}/>}
      </main>

      <footer className="footer">
        <div className="footer-in">
          <div className="footer-grid">
            <div>
              <h4>{site.name} {site.subtitle}</h4>
              <p style={{marginBottom:10}}>{site.tagline}</p>
              <p>Empowering students with expert coaching, structured learning, and personal care since day one.</p>
            </div>
            <div><h4>Quick Links</h4><ul className="fl">{navItems.map(n=><li key={n.id}><a onClick={()=>nav(n.id)}>{n.label}</a></li>)}</ul></div>
            <div><h4>Contact</h4><p>{site.address}</p>{site.phone&&<p style={{marginTop:10}}>📞 {site.phone}</p>}{site.email&&<p style={{marginTop:4}}>✉ {site.email}</p>}</div>
          </div>
          <div className="footer-bot">© 2026 {site.name}. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}

// ─── HOME PAGE ───
function PHome({site,faculty,courses,testimonials,nav}) {
  return <div className="fade">
    {/* HERO */}
    <section className="hero">
      <div className="hero-bg">
        <img src={site.heroImage || "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1400&q=80"} alt="Students" />
      </div>
      <div className="hero-in">
        {site.heroBadge&&<div className="hero-badge"><span className="hero-dot"/>{site.heroBadge}</div>}
        <h1>{site.heroTitle}</h1>
        <p className="hero-sub">{site.heroSubtitle}</p>
        <div className="hero-btns">
          <button className="bp" onClick={()=>nav("contact")}>Get Started <I.Arrow/></button>
          <button className="bo" onClick={()=>nav("courses")}>Explore Courses</button>
        </div>
      </div>
    </section>

    {/* STATS */}
    <div className="stats-bar">
      <div className="stat-i"><div className="stat-n">{site.stat1}</div><div className="stat-l">{site.stat1Label}</div></div>
      <div className="stat-i"><div className="stat-n">{site.stat2}</div><div className="stat-l">{site.stat2Label}</div></div>
      <div className="stat-i"><div className="stat-n">{site.stat3}</div><div className="stat-l">{site.stat3Label}</div></div>
      <div className="stat-i"><div className="stat-n">{site.stat4||"15+"}</div><div className="stat-l">{site.stat4Label||"Expert Faculty"}</div></div>
    </div>

    {/* WHY US */}
    <section className="sec">
      <div className="sec-in">
        <div className="sec-hd center"><div className="sec-lab">Why Guidance Wing</div><h2>What Makes Us Different</h2><p>We don't just teach — we transform. Here's what sets us apart from every other coaching centre.</p></div>
        <div className="hl-grid">
          <div className="hl-c"><div className="hl-num">01</div><h3>{site.highlight1Title}</h3><p>{site.highlight1Desc}</p></div>
          <div className="hl-c"><div className="hl-num">02</div><h3>{site.highlight2Title}</h3><p>{site.highlight2Desc}</p></div>
          <div className="hl-c"><div className="hl-num">03</div><h3>{site.highlight3Title}</h3><p>{site.highlight3Desc}</p></div>
        </div>
      </div>
    </section>

    {/* COURSES PREVIEW */}
    <section className="sec" style={{background:'var(--g50)'}}>
      <div className="sec-in">
        <div className="sec-hd center"><div className="sec-lab">Programs</div><h2>Courses We Offer</h2></div>
        <div className="co-grid">{courses.map((c,i)=><CourseCard key={i} c={c}/>)}</div>
      </div>
    </section>

    {/* FACULTY PREVIEW */}
    {faculty.length>0&&<section className="sec">
      <div className="sec-in">
        <div className="sec-hd center"><div className="sec-lab">Our Team</div><h2>Learn From the Best</h2><p>Our faculty brings decades of combined experience in teaching, research, and mentoring.</p></div>
        <div className="fac-grid">{faculty.map((f,i)=><div key={i} className="fac-c">
          <div className="fac-img">{f.photo?<img src={f.photo} alt={f.name}/>:<div className="fac-ph">{f.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>}</div>
          <h4>{f.name}</h4><div className="fac-sub">{f.subject}</div><div className="fac-qual">{f.qualification}</div>
        </div>)}</div>
      </div>
    </section>}

    {/* TESTIMONIALS */}
    {testimonials.length>0&&<section className="sec" style={{background:'var(--g50)'}}>
      <div className="sec-in">
        <div className="sec-hd center"><div className="sec-lab">Testimonials</div><h2>Hear From Our Students</h2></div>
        <div className="test-grid">{testimonials.slice(0,3).map((t,i)=><div key={i} className="test-c">
          <div className="test-stars">{[...Array(t.rating||5)].map((_,j)=><I.Star key={j}/>)}</div>
          <p className="test-txt">{t.text}</p>
          <div className="test-au"><div className="test-av">{t.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}</div><div><div className="test-nm">{t.name}</div><div className="test-cl">{t.cls}</div></div></div>
        </div>)}</div>
      </div>
    </section>}

    {/* CTA */}
    <section className="sec">
      <div className="cta-banner">
        <h2>Ready to Start Your Journey?</h2>
        <p>Limited seats available for the upcoming batch. Secure your spot today and take the first step toward academic excellence.</p>
        <div style={{display:'flex',gap:14,justifyContent:'center',flexWrap:'wrap'}}>
          <button className="bp" onClick={()=>nav("contact")}>Apply for Admission</button>
          {site.phone&&<a href={`tel:${site.phone}`} style={{padding:'16px 36px',background:'rgba(255,255,255,.15)',color:'#fff',border:'1px solid rgba(255,255,255,.3)',borderRadius:12,fontSize:16,fontWeight:600,textDecoration:'none',display:'inline-flex',alignItems:'center',gap:8,fontFamily:'var(--fb)',cursor:'pointer'}}><I.Phone/> {site.phone}</a>}
        </div>
      </div>
    </section>
  </div>;
}

function CourseCard({c}) {
  const feats = (c.features||'').split('\n').filter(Boolean);
  const subjs = (c.subjects||'').split(',').map(s=>s.trim()).filter(Boolean);
  return <div className={`co-c ${c.badge?'feat':''}`}>
    {c.badge&&<div className="co-badge">{c.badge}</div>}
    <div className="co-top"><h3>{c.cls}</h3><p>{c.tagline}</p></div>
    <div className="co-body">
      <div className="co-subjs">{subjs.map(s=><span key={s} className="co-st">{s}</span>)}</div>
      <ul className="co-feats">{feats.map((f,j)=><li key={j}><I.Check/>{f}</li>)}</ul>
      <div className="co-meta"><span className="co-time"><I.Clock/>{c.timing}</span><span className="co-fee">{c.fee}</span></div>
    </div>
  </div>;
}

// ─── ABOUT ───
function PAbout({site,faculty}) {
  return <div className="fade" style={{paddingTop:72}}>
    <section className="sec"><div className="sec-in">
      <div className="about-grid">
        <div className="about-img"><img src={site.aboutImage || "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80"} alt="About"/></div>
        <div className="about-txt">
          <div className="sec-lab">About Us</div>
          <h2>{site.tagline}</h2>
          {(site.aboutText||'').split('\n').filter(Boolean).map((p,i)=><p key={i}>{p}</p>)}
        </div>
      </div>
    </div></section>
    <section className="sec" style={{background:'var(--g50)'}}><div className="sec-in">
      <div className="sec-hd center"><div className="sec-lab">Our Team</div><h2>Meet Our Faculty</h2></div>
      <div className="fac-grid">{faculty.map((f,i)=><div key={i} className="fac-c">
        <div className="fac-img">{f.photo?<img src={f.photo} alt={f.name}/>:<div className="fac-ph">{f.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>}</div>
        <h4>{f.name}</h4><div className="fac-sub">{f.subject}</div><div className="fac-qual">{f.qualification}</div>
      </div>)}</div>
    </div></section>
  </div>;
}

// ─── COURSES ───
function PCourses({courses,nav}) {
  return <div className="fade" style={{paddingTop:72}}><section className="sec"><div className="sec-in">
    <div className="sec-hd center"><div className="sec-lab">Programs</div><h2>Courses Offered</h2></div>
    <div className="co-grid">{courses.map((c,i)=><CourseCard key={i} c={c}/>)}</div>
    <div style={{textAlign:'center',marginTop:40}}><button className="bp" onClick={()=>nav("contact")}>Enroll Now <I.Arrow/></button></div>
  </div></section></div>;
}

// ─── RESULTS ───
function PResults({results}) {
  const [f,setF]=useState("all");
  const cls=[...new Set(results.map(r=>r.cls))];
  const filtered=f==="all"?results:results.filter(r=>r.cls===f);
  return <div className="fade" style={{paddingTop:72}}><section className="sec"><div className="sec-in">
    <div className="sec-hd center"><div className="sec-lab">Achievements</div><h2>Our Student Results</h2></div>
    <div className="gal-filters" style={{justifyContent:'center'}}><button className={`gf ${f==='all'?'act':''}`} onClick={()=>setF('all')}>All</button>{cls.map(c=><button key={c} className={`gf ${f===c?'act':''}`} onClick={()=>setF(c)}>{c}</button>)}</div>
    <div className="res-grid">{filtered.map((r,i)=><div key={i} className="res-c">
      <div className="res-top"><div><div className="res-nm">{r.name}</div><div className="res-ex">{r.exam}</div></div><div className="res-cls">{r.cls}</div></div>
      <div className="res-sc">{r.score}</div><div className="res-rk">{r.rank}</div>
    </div>)}</div>
  </div></section></div>;
}

// ─── MATERIALS ───
function PMaterials({materials,studentPass,updateMaterials}) {
  const [auth,setAuth]=useState(null);
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const [cls,setCls]=useState("Class 11");
  const [subj,setSubj]=useState("Physics");
  const filtered=materials.filter(m=>m.cls===cls&&m.subject===subj);
  const login=()=>{if(pw===studentPass){setAuth(true);setErr("")}else setErr("Incorrect password")};
  if(!auth) return <div className="fade" style={{paddingTop:72}}><section className="sec"><div className="sec-in">
    <div className="mat-login"><div style={{marginBottom:16,color:'var(--pri-l)'}}><I.Lock/></div>
    <h3>Student Access</h3><p>Enter the password provided by your teacher.</p>
    <div className="ig"><label>Password</label><input type="password" className="inf" placeholder="Enter password" value={pw} onChange={e=>{setPw(e.target.value);setErr("")}} onKeyDown={e=>e.key==='Enter'&&login()}/>{err&&<div className="err">{err}</div>}</div>
    <button className="bf" onClick={login}>Access Materials</button></div>
  </div></section></div>;

  return <div className="fade" style={{paddingTop:72}}><section className="sec"><div className="sec-in">
    <div className="sec-hd center" style={{marginBottom:32}}><div className="sec-lab">Study Materials</div><h2>Daily Notes & Resources</h2></div>
    <div className="mat-panel">
      <div className="mat-sb">
        <h4>Class</h4>{CLASSES_LIST.map(c=><div key={c} className={`sb-i ${cls===c?'act':''}`} onClick={()=>setCls(c)}><I.Book/>{c}</div>)}
        <h4 style={{marginTop:18}}>Subject</h4>{SUBJECTS_LIST.map(s=><div key={s} className={`sb-i ${subj===s?'act':''}`} onClick={()=>setSubj(s)}>{s}</div>)}
        <div style={{marginTop:18,borderTop:'1px solid var(--g200)',paddingTop:12}}><button className="btn btn-o" style={{width:'100%',justifyContent:'center'}} onClick={()=>setAuth(null)}><I.Lock/> Logout</button></div>
      </div>
      <div className="mat-ct">
        <div className="mat-hdr"><h3>{cls} · {subj}</h3></div>
        {filtered.length===0?<div className="empty"><I.File/><p style={{marginTop:10}}>No materials yet for {cls} — {subj}</p></div>
        :filtered.map(m=><div key={m.id} style={{border:'1px solid var(--g200)',borderRadius:14,padding:18,marginBottom:10,transition:'.2s'}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
            <div style={{width:40,height:40,borderRadius:10,background:'var(--pri-50)',color:'var(--pri-l)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><I.File/></div>
            <div style={{flex:1}}>
              <div style={{fontSize:15,fontWeight:600,color:'var(--g900)',fontFamily:'var(--fh)'}}>{m.title}</div>
              <div style={{fontSize:12,color:'var(--g400)',marginTop:2}}>{m.type} · {m.date}{m.description?` · ${m.description}`:''}</div>
              {m.files&&m.files.length>0&&<div style={{marginTop:10,display:'flex',flexDirection:'column',gap:6}}>
                {m.files.map((f,j)=><div key={j} style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  <a href={f.url} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 16px',background:'var(--pri-l)',color:'#fff',borderRadius:10,fontSize:13,fontWeight:600,textDecoration:'none',transition:'.2s'}}>
                    {f.isPdf||f.name?.toLowerCase().endsWith('.pdf')?'📄':f.type?.includes('image')?'🖼️':'📎'} {f.name} — View
                  </a>
                  {f.downloadUrl&&<a href={f.downloadUrl} target="_blank" rel="noreferrer" download style={{display:'inline-flex',alignItems:'center',gap:4,padding:'8px 14px',background:'var(--g100)',color:'var(--g700)',borderRadius:10,fontSize:12,fontWeight:500,textDecoration:'none'}}>Download</a>}
                </div>)}
              </div>}
              {(!m.files||m.files.length===0)&&<div style={{marginTop:6,fontSize:12,color:'var(--g400)',fontStyle:'italic'}}>No files attached</div>}
            </div>
          </div>
        </div>)}
      </div>
    </div>
  </div></section></div>;
}

// ─── GALLERY ───
function PGallery({gallery}) {
  const [f,setF]=useState("all");
  const cats=[...new Set(gallery.map(g=>g.category))];
  const filtered=f==="all"?gallery:gallery.filter(g=>g.category===f);
  return <div className="fade" style={{paddingTop:72}}><section className="sec"><div className="sec-in">
    <div className="sec-hd center"><div className="sec-lab">Gallery</div><h2>Life at Our Centre</h2></div>
    <div className="gal-filters" style={{justifyContent:'center'}}><button className={`gf ${f==='all'?'act':''}`} onClick={()=>setF('all')}>All</button>{cats.map(c=><button key={c} className={`gf ${f===c?'act':''}`} onClick={()=>setF(c)}>{c[0].toUpperCase()+c.slice(1)}</button>)}</div>
    <div className="gal-grid">{filtered.map((g,i)=><div key={i} className="gal-i">
      {g.photo?<img src={g.photo} alt={g.caption}/>:<div className="gal-ph">📷</div>}
      <div className="gal-ov">{g.caption}</div>
    </div>)}</div>
  </div></section></div>;
}

// ─── TESTIMONIALS ───
function PTestimonials({testimonials}) {
  return <div className="fade" style={{paddingTop:72}}><section className="sec"><div className="sec-in">
    <div className="sec-hd center"><div className="sec-lab">Reviews</div><h2>What Students Say</h2></div>
    <div className="test-grid">{testimonials.map((t,i)=><div key={i} className="test-c">
      <div className="test-stars">{[...Array(t.rating||5)].map((_,j)=><I.Star key={j}/>)}</div>
      <p className="test-txt">{t.text}</p>
      <div className="test-au"><div className="test-av">{t.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}</div><div><div className="test-nm">{t.name}</div><div className="test-cl">{t.cls}</div></div></div>
    </div>)}</div>
  </div></section></div>;
}

// ─── CONTACT ───
function PContact({site,enquiries,updateEnquiries}) {
  const [ok,setOk]=useState(false);
  const [fm,setFm]=useState({name:'',phone:'',cls:'Class 11',message:''});
  const submit=()=>{
    if(!fm.name.trim()||!fm.phone.trim())return;
    const e={...fm,id:Date.now().toString(),date:new Date().toLocaleDateString('en-IN')};
    updateEnquiries([e,...enquiries]);
    setOk(true);setTimeout(()=>setOk(false),4000);
    setFm({name:'',phone:'',cls:'Class 11',message:''});
  };
  return <div className="fade" style={{paddingTop:72}}><section className="sec"><div className="sec-in">
    <div className="sec-hd center"><div className="sec-lab">Get in Touch</div><h2>Contact & Admissions</h2></div>
    <div className="ct-grid">
      <div className="ct-info"><h3>Visit Us</h3><p>We'd love to hear from you. Whether you have questions about our programs, want to visit our centre, or are ready to enroll — reach out.</p>
        {site.address&&<div className="ct-det"><div className="ct-det-ic"><I.Map/></div><div><h4>Address</h4><p>{site.address}</p></div></div>}
        {site.phone&&<div className="ct-det"><div className="ct-det-ic"><I.Phone/></div><div><h4>Phone</h4><p>{site.phone}</p></div></div>}
        {site.email&&<div className="ct-det"><div className="ct-det-ic"><I.File/></div><div><h4>Email</h4><p>{site.email}</p></div></div>}
      </div>
      <div className="ct-form"><h3>Enquiry Form</h3>
        {ok&&<div className="succ">✅ Thank you! We'll get back to you soon.</div>}
        <div className="f-row"><div className="ig"><label>Student Name *</label><input className="inf" placeholder="Full name" value={fm.name} onChange={e=>setFm({...fm,name:e.target.value})}/></div><div className="ig"><label>Phone *</label><input className="inf" placeholder="Phone number" value={fm.phone} onChange={e=>setFm({...fm,phone:e.target.value})}/></div></div>
        <div className="ig"><label>Class</label><select className="inf" value={fm.cls} onChange={e=>setFm({...fm,cls:e.target.value})}>{CLASSES_LIST.map(c=><option key={c}>{c}</option>)}</select></div>
        <div className="ig"><label>Message</label><textarea className="inf" placeholder="Any questions or special requirements..." value={fm.message} onChange={e=>setFm({...fm,message:e.target.value})}/></div>
        <button className="bf" onClick={submit}>Submit Enquiry</button>
      </div>
    </div>
  </div></section></div>;
}

function AdminApp({ data, updaters }) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [un, setUn] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("site");

  const login = () => {
    if (un === data.adminCreds.username && pw === data.adminCreds.password) { setLoggedIn(true); setErr(""); }
    else setErr("Invalid username or password");
  };

  if (!loggedIn) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'var(--g50)'}}>
      <div className="mat-login" style={{maxWidth:400}}>
        <div style={{marginBottom:16,color:'var(--pri-l)'}}><I.Shield/></div>
        <h3>Admin Login</h3><p>Enter your credentials to manage the website.</p>
        <div className="ig"><label>Username</label><input className="inf" placeholder="Username" value={un} onChange={e=>{setUn(e.target.value);setErr("")}} /></div>
        <div className="ig"><label>Password</label><input type="password" className="inf" placeholder="Password" value={pw} onChange={e=>{setPw(e.target.value);setErr("")}} onKeyDown={e=>e.key==='Enter'&&login()}/>{err&&<div className="err">{err}</div>}</div>
        <button className="bf" onClick={login}>Login</button>
        <p style={{marginTop:16,fontSize:12,color:'var(--g400)',cursor:'pointer'}} onClick={()=>{window.location.hash='';window.location.reload()}}>← Back to website</p>
      </div>
    </div>
  );

  const tabs = [
    {id:"site",label:"Site Settings",icon:<I.Settings/>},
    {id:"faculty",label:"Faculty",icon:<I.Users/>},
    {id:"courses",label:"Courses",icon:<I.Book/>},
    {id:"results",label:"Results",icon:<I.Award/>},
    {id:"materials",label:"Materials",icon:<I.File/>},
    {id:"testimonials",label:"Testimonials",icon:<I.Msg/>},
    {id:"gallery",label:"Gallery",icon:<I.Image/>},
    {id:"enquiries",label:"Enquiries",icon:<I.Inbox/>},
    {id:"security",label:"Security",icon:<I.Shield/>},
  ];

  return (
    <div className="admin-wrap">
      <div className="admin-sb">
        <div className="admin-sb-hd"><h3>Admin Panel</h3><p>{data.site.name}</p></div>
        <div className="admin-nav">{tabs.map(t=><div key={t.id} className={`an-i ${tab===t.id?'act':''}`} onClick={()=>setTab(t.id)}>{t.icon}{t.label}</div>)}</div>
        <div className="admin-sb-ft">
          <div className="an-i" onClick={()=>{window.location.hash='';window.location.reload()}}><I.Eye/> View Site</div>
          <div className="an-i" onClick={()=>setLoggedIn(false)}><I.Lock/> Logout</div>
        </div>
      </div>
      <div className="admin-main">
        {tab==="site"&&<AdminSite site={data.site} update={updaters.updateSite}/>}
        {tab==="faculty"&&<AdminList title="Faculty" items={data.faculty} update={updaters.updateFaculty} fields={[{k:'name',l:'Name'},{k:'subject',l:'Subject'},{k:'qualification',l:'Qualification'}]} hasPhoto/>}
        {tab==="courses"&&<AdminCourses courses={data.courses} update={updaters.updateCourses}/>}
        {tab==="results"&&<AdminList title="Student Results" items={data.results} update={updaters.updateResults} fields={[{k:'name',l:'Student Name'},{k:'exam',l:'Exam'},{k:'score',l:'Score'},{k:'cls',l:'Class',type:'select',opts:CLASSES_LIST},{k:'rank',l:'Rank/Achievement'}]}/>}
        {tab==="materials"&&<AdminMaterials materials={data.materials} update={updaters.updateMaterials}/>}
        {tab==="testimonials"&&<AdminList title="Testimonials" items={data.testimonials} update={updaters.updateTestimonials} fields={[{k:'name',l:'Student Name'},{k:'cls',l:'Class/Year'},{k:'text',l:'Testimonial Text',type:'textarea'},{k:'rating',l:'Rating (1-5)',type:'number'}]}/>}
        {tab==="gallery"&&<AdminGallery gallery={data.gallery} update={updaters.updateGallery}/>}
        {tab==="enquiries"&&<AdminEnquiries enquiries={data.enquiries} update={updaters.updateEnquiries}/>}
        {tab==="security"&&<AdminSecurity adminCreds={data.adminCreds} studentPass={data.studentPass} updateAdmin={updaters.updateAdminCreds} updateStudent={updaters.updateStudentPass}/>}
      </div>
    </div>
  );
}

// ─── ADMIN: Site Settings ───
function AdminSite({site,update}) {
  const [s,setS]=useState({...site});
  const [saved,setSaved]=useState(false);
  const save=()=>{update(s);setSaved(true);setTimeout(()=>setSaved(false),2000)};
  const ch=(k,v)=>setS({...s,[k]:v});
  return <div className="fade">
    <h2>Site Settings</h2>{saved&&<div className="succ">✅ Saved!</div>}
    <div className="a-card"><h4>Brand & Identity</h4>
      <div className="a-row"><div className="ig"><label>Centre Name</label><input className="inf" value={s.name} onChange={e=>ch('name',e.target.value)}/></div><div className="ig"><label>Subtitle</label><input className="inf" value={s.subtitle} onChange={e=>ch('subtitle',e.target.value)}/></div></div>
      <div className="ig"><label>Tagline</label><input className="inf" value={s.tagline} onChange={e=>ch('tagline',e.target.value)}/></div>
    </div>
    <div className="a-card"><h4>Hero Section</h4>
      <div className="ig"><label>Hero Title</label><input className="inf" value={s.heroTitle} onChange={e=>ch('heroTitle',e.target.value)}/></div>
      <div className="ig"><label>Hero Subtitle</label><textarea className="inf" value={s.heroSubtitle} onChange={e=>ch('heroSubtitle',e.target.value)}/></div>
      <div className="ig"><label>Badge Text (leave empty to hide)</label><input className="inf" value={s.heroBadge} onChange={e=>ch('heroBadge',e.target.value)}/></div>
      <div className="a-row-3">
        <div className="ig"><label>Stat 1 Number</label><input className="inf" value={s.stat1} onChange={e=>ch('stat1',e.target.value)}/></div>
        <div className="ig"><label>Stat 1 Label</label><input className="inf" value={s.stat1Label} onChange={e=>ch('stat1Label',e.target.value)}/></div>
        <div/>
      </div>
      <div className="a-row-3">
        <div className="ig"><label>Stat 2 Number</label><input className="inf" value={s.stat2} onChange={e=>ch('stat2',e.target.value)}/></div>
        <div className="ig"><label>Stat 2 Label</label><input className="inf" value={s.stat2Label} onChange={e=>ch('stat2Label',e.target.value)}/></div>
        <div/>
      </div>
      <div className="a-row-3">
        <div className="ig"><label>Stat 3 Number</label><input className="inf" value={s.stat3} onChange={e=>ch('stat3',e.target.value)}/></div>
        <div className="ig"><label>Stat 3 Label</label><input className="inf" value={s.stat3Label} onChange={e=>ch('stat3Label',e.target.value)}/></div>
        <div/>
      </div>
    </div>
    <div className="a-card"><h4>Highlight Cards</h4>
      {[1,2,3].map(n=><div key={n} className="a-row"><div className="ig"><label>Highlight {n} Title</label><input className="inf" value={s[`highlight${n}Title`]} onChange={e=>ch(`highlight${n}Title`,e.target.value)}/></div><div className="ig"><label>Highlight {n} Description</label><input className="inf" value={s[`highlight${n}Desc`]} onChange={e=>ch(`highlight${n}Desc`,e.target.value)}/></div></div>)}
    </div>
    <div className="a-card"><h4>About Section</h4>
      <div className="ig"><label>About Text (use blank lines for paragraphs)</label><textarea className="inf" style={{minHeight:120}} value={s.aboutText} onChange={e=>ch('aboutText',e.target.value)}/></div>
    </div>
    <div className="a-card"><h4>Contact Information</h4>
      <div className="a-row-3"><div className="ig"><label>Phone</label><input className="inf" value={s.phone} onChange={e=>ch('phone',e.target.value)}/></div><div className="ig"><label>Email</label><input className="inf" value={s.email} onChange={e=>ch('email',e.target.value)}/></div><div/></div>
      <div className="ig"><label>Address</label><textarea className="inf" value={s.address} onChange={e=>ch('address',e.target.value)}/></div>
    </div>
    <button className="btn btn-p" onClick={save}><I.Check/> Save All Settings</button>
  </div>;
}

// ─── ADMIN: Generic List (Faculty, Results, Testimonials) ───
function AdminList({title,items,update,fields,hasPhoto}) {
  const [list,setList]=useState([...items]);
  const [editing,setEditing]=useState(null);
  const [saved,setSaved]=useState(false);
  const save=(l)=>{update(l);setSaved(true);setTimeout(()=>setSaved(false),2000)};
  const add=()=>{const n={id:Date.now().toString()};fields.forEach(f=>n[f.k]='');if(hasPhoto)n.photo='';setList([...list,n]);setEditing(n.id)};
  const del=(id)=>{const l=list.filter(i=>i.id!==id);setList(l);save(l)};
  const ch=(id,k,v)=>setList(list.map(i=>i.id===id?{...i,[k]:v}:i));
  const saveAll=()=>{save(list);setEditing(null)};

  return <div className="fade">
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
      <h2>{title}</h2><button className="btn btn-p" onClick={add}><I.Plus/> Add New</button>
    </div>{saved&&<div className="succ">✅ Saved!</div>}
    {list.length===0?<div className="empty"><p>No {title.toLowerCase()} added yet.</p></div>:
    list.map(item=><div key={item.id} className="a-card">
      {editing===item.id?<>
        <div className="a-row">{fields.map(f=><div key={f.k} className="ig"><label>{f.l}</label>
          {f.type==='textarea'?<textarea className="inf" value={item[f.k]||''} onChange={e=>ch(item.id,f.k,e.target.value)}/>
          :f.type==='select'?<select className="inf" value={item[f.k]||''} onChange={e=>ch(item.id,f.k,e.target.value)}><option value="">Select...</option>{f.opts.map(o=><option key={o}>{o}</option>)}</select>
          :f.type==='number'?<input type="number" className="inf" value={item[f.k]||''} onChange={e=>ch(item.id,f.k,parseInt(e.target.value)||0)}/>
          :<input className="inf" value={item[f.k]||''} onChange={e=>ch(item.id,f.k,e.target.value)}/>}
        </div>)}</div>
        {hasPhoto&&<div className="ig" style={{maxWidth:200}}><label>Photo</label><ImageUpload value={item.photo} onChange={v=>ch(item.id,'photo',v)}/></div>}
        <div style={{display:'flex',gap:8,marginTop:8}}><button className="btn btn-p" onClick={saveAll}><I.Check/> Save</button><button className="btn btn-o" onClick={()=>setEditing(null)}>Cancel</button></div>
      </>:<div className="a-list-i">
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          {hasPhoto&&item.photo&&<img src={item.photo} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover'}}/>}
          <div><strong style={{fontSize:14}}>{item[fields[0].k]||'(untitled)'}</strong>
          {fields[1]&&<span style={{fontSize:12,color:'var(--g500)',marginLeft:8}}>{item[fields[1].k]}</span>}</div>
        </div>
        <div style={{display:'flex',gap:6}}><button className="btn btn-o" onClick={()=>setEditing(item.id)}><I.Edit/></button><button className="btn btn-d" onClick={()=>del(item.id)}><I.Trash/></button></div>
      </div>}
    </div>)}
  </div>;
}

// ─── ADMIN: Courses ───
function AdminCourses({courses,update}) {
  const [list,setList]=useState([...courses]);
  const [editing,setEditing]=useState(null);
  const [saved,setSaved]=useState(false);
  const save=(l)=>{update(l);setSaved(true);setTimeout(()=>setSaved(false),2000)};
  const add=()=>{const n={id:Date.now().toString(),cls:'',tagline:'',subjects:'',features:'',timing:'',fee:'',badge:''};setList([...list,n]);setEditing(n.id)};
  const del=(id)=>{const l=list.filter(i=>i.id!==id);setList(l);save(l)};
  const ch=(id,k,v)=>setList(list.map(i=>i.id===id?{...i,[k]:v}:i));
  const saveAll=()=>{save(list);setEditing(null)};

  return <div className="fade">
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
      <h2>Courses</h2><button className="btn btn-p" onClick={add}><I.Plus/> Add Course</button>
    </div>{saved&&<div className="succ">✅ Saved!</div>}
    {list.map(c=><div key={c.id} className="a-card">
      {editing===c.id?<>
        <div className="a-row">
          <div className="ig"><label>Class (e.g. Class 11)</label><input className="inf" value={c.cls} onChange={e=>ch(c.id,'cls',e.target.value)}/></div>
          <div className="ig"><label>Tagline</label><input className="inf" value={c.tagline} onChange={e=>ch(c.id,'tagline',e.target.value)}/></div>
        </div>
        <div className="ig"><label>Subjects (comma-separated)</label><input className="inf" value={c.subjects} onChange={e=>ch(c.id,'subjects',e.target.value)}/></div>
        <div className="ig"><label>Features (one per line)</label><textarea className="inf" style={{minHeight:100}} value={c.features} onChange={e=>ch(c.id,'features',e.target.value)}/></div>
        <div className="a-row-3">
          <div className="ig"><label>Timing</label><input className="inf" value={c.timing} onChange={e=>ch(c.id,'timing',e.target.value)}/></div>
          <div className="ig"><label>Fee</label><input className="inf" value={c.fee} onChange={e=>ch(c.id,'fee',e.target.value)}/></div>
          <div className="ig"><label>Badge (optional)</label><input className="inf" value={c.badge} onChange={e=>ch(c.id,'badge',e.target.value)}/></div>
        </div>
        <div style={{display:'flex',gap:8,marginTop:8}}><button className="btn btn-p" onClick={saveAll}><I.Check/> Save</button><button className="btn btn-o" onClick={()=>setEditing(null)}>Cancel</button></div>
      </>:<div className="a-list-i">
        <div><strong>{c.cls}</strong><span style={{fontSize:12,color:'var(--g500)',marginLeft:8}}>{c.tagline}</span>{c.badge&&<span className="a-tag" style={{marginLeft:8}}>{c.badge}</span>}</div>
        <div style={{display:'flex',gap:6}}><button className="btn btn-o" onClick={()=>setEditing(c.id)}><I.Edit/></button><button className="btn btn-d" onClick={()=>del(c.id)}><I.Trash/></button></div>
      </div>}
    </div>)}
  </div>;
}

// ─── ADMIN: Materials ───
function AdminMaterials({materials,update}) {
  const [cls,setCls]=useState("Class 11");
  const [subj,setSubj]=useState("Physics");
  const [title,setTitle]=useState("");
  const [type,setType]=useState("notes");
  const [desc,setDesc]=useState("");
  const [files,setFiles]=useState([]);
  const [uploading,setUploading]=useState(false);
  const [progress,setProgress]=useState("");
  const [errMsg,setErrMsg]=useState("");
  const fileRef=useRef();
  const filtered=materials.filter(m=>m.cls===cls&&m.subject===subj);

  const handleFiles=(e)=>{
    const selected=[...e.target.files];
    if(selected.length>0) setFiles(prev=>[...prev,...selected]);
    e.target.value="";
  };
  const removeFile=(idx)=>setFiles(files.filter((_,i)=>i!==idx));

  const add=async()=>{
    if(!title.trim()){setErrMsg("Please enter a title");return;}
    setUploading(true);setErrMsg("");setProgress("Preparing upload...");
    const uploadedFiles=[];
    for(let i=0;i<files.length;i++){
      const f=files[i];
      setProgress("Uploading "+(i+1)+"/"+files.length+": "+f.name);
      try{
        const result=await uploadFile(f);
        if(result&&result.url){
          uploadedFiles.push({name:f.name,url:result.url,publicId:result.publicId,size:result.size,type:f.type});
        }else{
          setErrMsg("Failed to upload: "+f.name);
        }
      }catch(err){
        console.error("Upload failed:",err);
        setErrMsg("Error uploading "+f.name);
      }
    }
    const m={id:Date.now().toString(),title:title.trim(),type,description:desc.trim(),cls,subject:subj,date:new Date().toLocaleDateString("en-IN"),files:uploadedFiles};
    await update([m,...materials]);
    setTitle("");setDesc("");setType("notes");setFiles([]);setUploading(false);
    if(uploadedFiles.length>0) setProgress("\u2705 Uploaded successfully!");
    setTimeout(()=>setProgress(""),3000);
  };

  const del=(id)=>{update(materials.filter(m=>m.id!==id))};

  const formatSize=(bytes)=>{
    if(!bytes)return "";
    if(bytes<1024)return bytes+" B";
    if(bytes<1024*1024)return (bytes/1024).toFixed(1)+" KB";
    return (bytes/(1024*1024)).toFixed(1)+" MB";
  };

  return <div className="fade">
    <h2>Study Materials</h2>
    <div className="a-card"><h4>Upload New Material</h4>
      <div className="a-row">
        <div className="ig"><label>Class</label><select className="inf" value={cls} onChange={e=>setCls(e.target.value)}>{CLASSES_LIST.map(c=><option key={c}>{c}</option>)}</select></div>
        <div className="ig"><label>Subject</label><select className="inf" value={subj} onChange={e=>setSubj(e.target.value)}>{SUBJECTS_LIST.map(s=><option key={s}>{s}</option>)}</select></div>
      </div>
      <div className="a-row">
        <div className="ig"><label>Title *</label><input className="inf" placeholder="e.g. Chapter 5 Notes" value={title} onChange={e=>setTitle(e.target.value)}/></div>
        <div className="ig"><label>Type</label><select className="inf" value={type} onChange={e=>setType(e.target.value)}><option value="notes">Notes</option><option value="assignment">Assignment</option><option value="question-paper">Question Paper</option><option value="solution">Solution</option><option value="reference">Reference</option></select></div>
      </div>
      <div className="ig"><label>Description</label><input className="inf" placeholder="Brief description..." value={desc} onChange={e=>setDesc(e.target.value)}/></div>

      <div style={{marginTop:12,marginBottom:12}}>
        <label style={{fontSize:"12.5px",fontWeight:600,color:"var(--g700)",display:"block",marginBottom:8}}>Attach Files (PDFs, Photos, any type)</label>
        <input ref={fileRef} type="file" multiple onChange={handleFiles} style={{display:"none"}}/>
        <div style={{border:"2px dashed var(--g300)",borderRadius:12,padding:20,textAlign:"center",cursor:"pointer",transition:".2s"}} onClick={()=>fileRef.current?.click()}>
          <I.Upload/><p style={{fontSize:12,color:"var(--g400)",marginTop:4}}>Click to choose files (PDFs, images, docs)</p>
        </div>
        {files.length>0&&<div style={{marginTop:10}}>
          {files.map((f,i)=><div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"var(--g50)",borderRadius:8,marginBottom:4,border:"1px solid var(--g200)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:16}}>{f.type?.includes("pdf")?"\ud83d\udcc4":f.type?.includes("image")?"\ud83d\uddbc\ufe0f":"\ud83d\udcce"}</span>
              <div><div style={{fontSize:13,fontWeight:500,color:"var(--g800)"}}>{f.name}</div><div style={{fontSize:10.5,color:"var(--g400)"}}>{formatSize(f.size)}</div></div>
            </div>
            <button className="btn btn-d" style={{padding:"4px 8px"}} onClick={()=>removeFile(i)}><I.Trash/></button>
          </div>)}
          <div style={{fontSize:12,color:"var(--g500)",marginTop:6}}>{files.length} file(s) ready to upload</div>
        </div>}
      </div>

      {errMsg&&<div className="err" style={{marginBottom:10}}>{errMsg}</div>}
      {progress&&<div style={{padding:"8px 14px",background:"var(--pri-50)",borderRadius:8,fontSize:13,color:"var(--pri-l)",marginBottom:12}}>{progress}</div>}
      <button className="btn btn-p" onClick={add} disabled={uploading} style={{opacity:uploading?.6:1}}><I.Upload/> {uploading?"Uploading...":"Upload Material"}</button>
    </div>
    <h3 style={{marginTop:20}}>{cls} \u00b7 {subj} ({filtered.length} items)</h3>
    {filtered.length===0?<div className="empty"><p>No materials for this selection.</p></div>:
    filtered.map(m=><div key={m.id} className="a-card" style={{padding:16,marginBottom:8}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <strong style={{fontSize:14}}>{m.title}</strong>
          <span style={{fontSize:11,color:"var(--g400)",marginLeft:8}}>{m.type} \u00b7 {m.date}</span>
          {m.description&&<p style={{fontSize:12.5,color:"var(--g500)",marginTop:2}}>{m.description}</p>}
          {m.files&&m.files.length>0&&<div style={{marginTop:8}}>
            {m.files.map((f,j)=><a key={j} href={f.url} target="_blank" rel="noreferrer" style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",background:"var(--pri-50)",borderRadius:6,fontSize:12,color:"var(--pri-l)",textDecoration:"none",marginRight:6,marginBottom:4}}>
              {f.name}
            </a>)}
          </div>}
          {(!m.files||m.files.length===0)&&<div style={{fontSize:11,color:"var(--g400)",marginTop:4,fontStyle:"italic"}}>No files attached</div>}
        </div>
        <button className="btn btn-d" onClick={()=>del(m.id)}><I.Trash/></button>
      </div>
    </div>)}
  </div>;
}

// ─── ADMIN: Gallery ───
function AdminGallery({gallery,update}) {
  const [list,setList]=useState([...gallery]);
  const [saved,setSaved]=useState(false);
  const save=(l)=>{update(l);setSaved(true);setTimeout(()=>setSaved(false),2000)};
  const add=()=>{setList([...list,{id:Date.now().toString(),caption:'',category:'classroom',photo:''}])};
  const del=(id)=>{const l=list.filter(i=>i.id!==id);setList(l);save(l)};
  const ch=(id,k,v)=>setList(list.map(i=>i.id===id?{...i,[k]:v}:i));

  return <div className="fade">
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
      <h2>Gallery</h2><button className="btn btn-p" onClick={add}><I.Plus/> Add Photo</button>
    </div>{saved&&<div className="succ">✅ Saved!</div>}
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
      {list.map(g=><div key={g.id} className="a-card" style={{padding:16}}>
        <ImageUpload value={g.photo} onChange={v=>ch(g.id,'photo',v)} label="Upload gallery photo"/>
        <div className="ig" style={{marginTop:10}}><input className="inf" placeholder="Caption" value={g.caption} onChange={e=>ch(g.id,'caption',e.target.value)}/></div>
        <div className="ig"><select className="inf" value={g.category} onChange={e=>ch(g.id,'category',e.target.value)}>
          <option value="classroom">Classroom</option><option value="lab">Lab</option><option value="events">Events</option><option value="students">Students</option><option value="other">Other</option>
        </select></div>
        <div style={{display:'flex',gap:6}}><button className="btn btn-p" style={{flex:1}} onClick={()=>save(list)}><I.Check/> Save</button><button className="btn btn-d" onClick={()=>del(g.id)}><I.Trash/></button></div>
      </div>)}
    </div>
  </div>;
}

// ─── ADMIN: Enquiries ───
function AdminEnquiries({enquiries,update}) {
  const del=(id)=>update(enquiries.filter(e=>e.id!==id));
  return <div className="fade">
    <h2>Enquiries ({enquiries.length})</h2>
    {enquiries.length===0?<div className="empty"><p>No enquiries received yet.</p></div>:
    enquiries.map(e=><div key={e.id} className="a-card" style={{padding:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div><strong>{e.name}</strong><span className="a-tag" style={{marginLeft:8}}>{e.cls}</span><p style={{fontSize:13,color:'var(--g500)',marginTop:4}}>📞 {e.phone} · {e.date}</p>{e.message&&<p style={{fontSize:13,color:'var(--g600)',marginTop:6,fontStyle:'italic'}}>{e.message}</p>}</div>
        <button className="btn btn-d" onClick={()=>del(e.id)}><I.Trash/></button>
      </div>
    </div>)}
  </div>;
}

// ─── ADMIN: Security ───
function AdminSecurity({adminCreds,studentPass,updateAdmin,updateStudent}) {
  const [a,setA]=useState({...adminCreds});
  const [sp,setSp]=useState(studentPass);
  const [saved,setSaved]=useState(false);
  const save=()=>{updateAdmin(a);updateStudent(sp);setSaved(true);setTimeout(()=>setSaved(false),2000)};
  return <div className="fade">
    <h2>Security Settings</h2>{saved&&<div className="succ">✅ Saved!</div>}
    <div className="a-card"><h4>Admin Credentials</h4>
      <div className="a-row"><div className="ig"><label>Username</label><input className="inf" value={a.username} onChange={e=>setA({...a,username:e.target.value})}/></div><div className="ig"><label>Password</label><input className="inf" value={a.password} onChange={e=>setA({...a,password:e.target.value})}/></div></div>
    </div>
    <div className="a-card"><h4>Student Materials Password</h4><p style={{fontSize:13,color:'var(--g500)',marginBottom:12}}>Students use this password to access study materials.</p>
      <div className="ig"><label>Password</label><input className="inf" value={sp} onChange={e=>setSp(e.target.value)}/></div>
    </div>
    <button className="btn btn-p" onClick={save}><I.Check/> Save Security Settings</button>
  </div>;
}
